import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Query, QueryGetVisitorGroupsForTenantArgs } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS } from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';
import { filterHiddenVisitorGroups } from 'v2/utils/visitorGroupOrigin';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// Only the member ids are selected (`memberIds`, not `members`), so no nested resolver runs.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;

const useVisitorsGroups = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: allVisitorGroupsLoading,
    error: allVisitorGroupsError,
    data: allVisitorGroupsData,
    refetch: refetchAllVisitorGroups
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, { notifyOnNetworkStatusChange: true });

  useEffect(() => {
    if (allVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(allVisitorGroupsError);
    }
  }, [allVisitorGroupsError]);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  useRegisterBackgroundRefetch(!!allVisitorGroupsData && allVisitorGroupsLoading);

  const allVisitorGroups = useMemo(
    () => filterHiddenVisitorGroups(allVisitorGroupsData?.getVisitorGroupsForTenant ?? []),
    [allVisitorGroupsData]
  );

  return {
    allVisitorGroups,
    loadingAllVisitorGroups: allVisitorGroupsLoading && !allVisitorGroupsData,
    hasVisitorGroupsFetchError: Boolean(allVisitorGroupsError),
    // A failed retry is already reported through `allVisitorGroupsError`, so the rejection is only swallowed here.
    retryFetchingVisitorGroups: () => refetchAllVisitorGroups().catch(() => undefined)
  };
};

export default useVisitorsGroups;
